import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Landmark } from "lucide-react";
import { useI18n, SUPPORTED_LOCALES } from "@/i18n/I18nContext";

const NAV_LINKS = [
  { key: "nav.services", to: "/#services" },
  { key: "nav.guides", to: "/#guides" },
  { key: "nav.about", to: "/#about" },
  { key: "nav.contact", to: "/#newsletter" },
];

export default function Header() {
  const { locale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const buildPath = (to) => {
    const [path, hash = ""] = to.split("#");
    const base = path === "/" ? `/${locale}` : `/${locale}${path}`;
    return hash ? `${base}#${hash}` : base;
  };

  const switchLanguage = (target) => {
    if (target === locale) return;
    const restOfPath = location.pathname.replace(/^\/(mx|en)(?=\/|$)/i, "") || "/";
    const newPath = restOfPath === "/" ? `/${target}` : `/${target}${restOfPath}`;
    navigate(`${newPath}${location.hash || ""}`, { replace: false });
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-[#dbe4ea] bg-[#0b3d5c] text-white"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
        <Link
          to={`/${locale}`}
          className="flex items-center gap-3"
          data-testid="header-logo-link"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0f5c3f] text-white">
            <Landmark size={20} strokeWidth={1.75} />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
              {t("brand.name")}
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#9dc4d8]">
              {t("brand.tagline")}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex" data-testid="header-nav-desktop">
          {NAV_LINKS.map((item) => (
            <Link
              key={item.key}
              to={buildPath(item.to)}
              className="text-sm font-medium text-white/85 hover:text-white"
              data-testid={`header-link-${item.key.replace("nav.", "")}`}
            >
              {t(item.key)}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <div
            className="flex items-center overflow-hidden rounded-full border border-white/30"
            data-testid="header-lang-switcher"
            role="group"
            aria-label={t("nav.language")}
          >
            {SUPPORTED_LOCALES.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => switchLanguage(code)}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${
                  locale === code ? "bg-white text-[#0b3d5c]" : "text-white/85 hover:text-white"
                }`}
                data-testid={`lang-switch-${code}`}
                aria-pressed={locale === code}
              >
                {code}
              </button>
            ))}
          </div>
          <Link
            to={buildPath("/#newsletter")}
            className="rounded-full bg-[#0f5c3f] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0d4d35]"
            data-testid="header-cta-consult"
          >
            {t("cta.consult")}
          </Link>
        </div>

        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 text-white md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
          data-testid="header-hamburger"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-[#0b3d5c] md:hidden" data-testid="header-mobile-panel">
          <nav className="flex flex-col px-5 py-4">
            {NAV_LINKS.map((item) => (
              <Link
                key={item.key}
                to={buildPath(item.to)}
                className="py-2 text-sm font-medium text-white/90"
                data-testid={`header-mobile-link-${item.key.replace("nav.", "")}`}
              >
                {t(item.key)}
              </Link>
            ))}
            <Link
              to={buildPath("/#newsletter")}
              className="mt-3 rounded-full bg-[#0f5c3f] px-5 py-2 text-center text-sm font-semibold text-white"
              data-testid="header-mobile-cta"
            >
              {t("cta.consult")}
            </Link>
            <div
              className="mt-4 flex items-center gap-2 border-t border-white/10 pt-4"
              data-testid="header-mobile-lang-switcher"
            >
              <span className="text-xs uppercase tracking-wider text-white/60">
                {t("nav.language")}
              </span>
              {SUPPORTED_LOCALES.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => switchLanguage(code)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
                    locale === code
                      ? "border-white bg-white text-[#0b3d5c]"
                      : "border-white/30 text-white/80"
                  }`}
                  data-testid={`header-mobile-lang-${code}`}
                >
                  {code}
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
